import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import SearchSharpIcon from '@mui/icons-material/SearchSharp';
import {
    Box,
    Button,
    Grid,
    IconButton,
    Input,
    InputAdornment,
    ListItemText,
    ListSubheader,
    MenuItem,
    Select,
    TextField,
    Theme,
    Typography,
    useTheme,
} from '@mui/material';
import { BookingSearchRequestData } from 'api/models/BookingSearch';
import { BookingSearchStatus } from 'api/models/Common/JyrneyEnums';
import useClients from 'hooks/useClients';
import { DateTime } from 'luxon';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { DateRangePicker } from 'react-dates';

type SearchBarProps = {
  defaultSearch: BookingSearchRequestData
  defaultClient: any
  updateSearch: any
}

const containsText = (text: string, searchText: string) =>
  text.toLowerCase().indexOf(searchText.toLowerCase()) > -1

const SearchBar = ({
  defaultSearch,
  defaultClient,
  updateSearch,
}: SearchBarProps) => {
  const theme = useTheme<Theme>()
  const styles = {
    searchContainer: {
      width: '100%',
      paddingBottom: '0px',
      backgroundColor: theme.components.BookingTable.headerColour,
      marginRight: 'var(--sp-12, 48px)',
    },
    searchGridItem: {
      paddingRight: 'var(--sp-2, 8px)',
    },
    searchInput: {
      height: '38px',
      width: '100%',
      paddingLeft: 'var(--sp-3, 12px)',
      paddingRight: 'var(--sp-2, 8px)',
      fontSize: 'var(--fs-14, 0.875rem)',
      verticalAlign: 'bottom',
      backgroundColor: 'var(--jy-white, #ffffff)',
      color: 'var(--jy-ink-900, #0d1418)',
      borderRadius: 'var(--r-sm, 6px)',
      border: '1px solid var(--jy-ink-200, #e2e6ea)',
      '&:before, &:after': { display: 'none' },
      '&:hover': {
        backgroundColor: 'var(--jy-white, #ffffff)',
        borderColor: 'var(--jy-ink-300, #c9d0d6)',
      },
      '&.Mui-focused': {
        borderColor: 'var(--jy-amber-500, #f4a94a)',
      },
      '& input::placeholder': {
        color: 'var(--jy-ink-500, #808b95)',
        opacity: 1,
      },
      '& .MuiInputAdornment-root .MuiSvgIcon-root': {
        color: 'var(--jy-ink-600, #5a6670)',
      },
    },
    searchSelect: {
      height: '38px',
      width: '100%',
      paddingTop: 'var(--sp-1, 4px)',
      paddingBottom: 'var(--sp-1, 4px)',
      fontSize: 'var(--fs-14, 0.875rem)',
    },
    fieldLabel: {
      color: 'var(--jy-ink-100, #eff1f4)',
      fontSize: 'var(--fs-13, 0.8125rem)',
      letterSpacing: 'var(--tracking-caps, 0.14em)',
      textTransform: 'uppercase',
      fontWeight: 600,
      textWrap: 'nowrap',
      px: 0,
      py: { xs: '0.25rem', md: '0.5rem' },
    },
  }

  const { options: clientOptions } = useClients()
  const [clientSelect, setClientSelect] = React.useState(
    defaultClient.companyName,
  )
  const [fromDate, setFromDate] = React.useState<moment.Moment | null>(
    moment(defaultSearch.when?.from),
  )
  const [toDate, setToDate] = React.useState<moment.Moment | null>(
    moment(defaultSearch.when?.to),
  )
  const [focusedInput, setFocusedInput] = React.useState<any>(null)
  const [searchText, setSearchText] = useState('')

  const [searchOptions, setSearchOptions] =
    React.useState<BookingSearchRequestData>(defaultSearch)

  const searchModeRef = useRef<'none' | 'ref' | 'text'>('none')

  const handleDatesChange = (dates: any) => {
    setFromDate(dates.startDate)
    setToDate(dates.endDate)
  }

  const updateSearchOptions = (update: object) => {
    setSearchOptions((prev) => ({ ...prev, ...update }))
  }

  useEffect(() => {
    const trimmedSearch = (searchOptions.search ?? '').trim()
    const trimmedInvoice = (searchOptions.invoiceReference ?? '').trim()
    const looksLikeBookingRef = /^[bj]\d/i.test(trimmedSearch)

    const mode =
      trimmedInvoice.length > 0 || looksLikeBookingRef
        ? 'ref'
        : trimmedSearch.length > 0
        ? 'text'
        : 'none'

    if (mode === searchModeRef.current) return
    searchModeRef.current = mode

    if (mode === 'ref') {
      setClientSelect('All')
      setFromDate(null)
      setToDate(null)
      updateSearchOptions({ status: null })
      return
    }

    if (mode === 'text') {
      setClientSelect('All')
      setFromDate(moment().subtract(6, 'months').startOf('day'))
      setToDate(moment().add(2, 'years'))
      return
    }

    setClientSelect(defaultClient.companyName)
    setFromDate(moment(defaultSearch.when?.from))
    setToDate(moment(defaultSearch.when?.to))
  }, [searchOptions.search, searchOptions.invoiceReference])

  useEffect(() => {
    const normalizedFrom = fromDate ? fromDate.clone().startOf('day') : null
    const normalizedTo = toDate ? toDate.clone().endOf('day') : null

    updateSearchOptions({
      when:
        normalizedFrom || normalizedTo
          ? {
              from: normalizedFrom
                ? DateTime.fromJSDate(normalizedFrom.toDate()).toISO()
                : null,
              to: normalizedTo
                ? DateTime.fromJSDate(normalizedTo.toDate()).toISO()
                : null,
            }
          : undefined,
    })
  }, [fromDate, toDate])

  useEffect(() => {
    updateSearch(searchOptions)
  }, [searchOptions])

  useEffect(() => {
    if (clientSelect === 'All') {
      updateSearchOptions({ clientName: null })
    } else {
      updateSearchOptions({ clientName: clientSelect })
    }
  }, [clientSelect, setClientSelect])

  return (
    <>
      <Grid
        container
        sx={{
          width: '100%',
          px: { xs: 1, md: 3 },
          py: { xs: 2, md: 2 },
          borderRadius: 'var(--r-lg, 14px) var(--r-lg, 14px) 0 0',
          justifyContent: { xs: 'flex-start', md: 'space-between' },
          backgroundColor: theme.components.BookingTable.headerColour,
        }}
        spacing={0}
        columns={12}
      >
        <Grid
          item
          xs={5}
          sm={8}
          md={4}
          lg={3}
          xl={2}
          sx={styles.searchGridItem}
        >
          <Typography sx={styles.fieldLabel}>Client:</Typography>
          <Select
            key="clientSelect"
            id="clientSelect"
            value={clientSelect}
            onChange={(e) => setClientSelect(e.target.value)}
            sx={styles.searchSelect}
          >
            <ListSubheader>
              <TextField
                size="small"
                placeholder="Type to search..."
                fullWidth
                value={searchText}
                autoComplete="off"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== 'Escape') {
                    // Prevents autoselecting item while typing (default Select behaviour)
                    e.stopPropagation()
                  }
                }}
              />
            </ListSubheader>
            <MenuItem key={'All'} value={'All'}>
              All
            </MenuItem>
            {/* <MenuItem key={clientSelect} value={clientSelect}>
      {clientSelect}
    </MenuItem> */}
            {/* Not being utilized, response from the API returns an empty array */}
            {clientOptions
              ? Object.entries(clientOptions)
                  .filter(
                    ([, label]) =>
                      containsText(label.toString(), searchText) ||
                      clientSelect === label,
                  )
                  .sort(([, labelA], [, labelB]) =>
                    labelA.toString().localeCompare(labelB.toString()),
                  )
                  .map(([value, label]) => {
                    return (
                      <MenuItem key={value} value={label}>
                        {label}
                      </MenuItem>
                    )
                  })
              : null}
          </Select>
        </Grid>
        <Grid
          item
          xs={5}
          sm={4}
          md={3}
          lg={3}
          xl={1}
          sx={styles.searchGridItem}
        >
          <Typography sx={styles.fieldLabel}>Status:</Typography>
          <Select
            key="statusSelect"
            id="statusSelect"
            value={searchOptions.status ? searchOptions.status : 'All'}
            onChange={(event) => {
              if (event.target.value === 'All') {
                updateSearchOptions({ status: null })
              } else {
                updateSearchOptions({ status: [event.target.value] })
              }
            }}
            sx={styles.searchSelect}
          >
            <MenuItem key={'All'} value={'All'}>
              All
            </MenuItem>
            {Object.entries(BookingSearchStatus)?.map(([value, label]) => (
              <MenuItem key={value} value={label}>
                <ListItemText primary={label} />
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid
          item
          xs={10}
          sm={6}
          md={5}
          lg={4}
          xl={2}
          sx={styles.searchGridItem}
        >
          <Typography sx={styles.fieldLabel}>Date Range:</Typography>
          <Box
            sx={{
              '& .DateRangePickerInput': {
                height: '38px',
                backgroundColor: 'var(--jy-white, #ffffff)',
                border: '1px solid var(--jy-ink-200, #e2e6ea)',
                borderRadius: 'var(--r-sm, 6px)',
                overflow: 'hidden',
              },
              '& .DateRangePickerInput:hover': {
                borderColor: 'var(--jy-ink-300, #c9d0d6)',
              },
              '& .DateInput, & .DateInput_input': {
                backgroundColor: 'transparent',
                color: 'var(--jy-ink-900, #0d1418)',
              },
              '& .DateInput_input': {
                fontSize: 'var(--fs-14, 0.875rem)',
              },
              '& .DateInput_input::placeholder': {
                color: 'var(--jy-ink-500, #808b95)',
                opacity: 1,
              },
              '& .DateRangePickerInput_calendarIcon svg': {
                fill: 'var(--jy-ink-600, #5a6670)',
              },
              '& .DateRangePickerInput_arrow svg': {
                fill: 'var(--jy-ink-500, #808b95)',
              },
            }}
          >
            <DateRangePicker
              startDate={fromDate}
              startDateId="tata-start-date"
              endDate={toDate}
              endDateId="tata-end-date"
              onDatesChange={handleDatesChange}
              focusedInput={focusedInput}
              onFocusChange={(focusedInput) => setFocusedInput(focusedInput)}
              displayFormat="DD MMM YY"
              hideKeyboardShortcutsPanel={true}
              isOutsideRange={() => false}
              showDefaultInputIcon
              withPortal
              readOnly
              regular
              small
            />
          </Box>
        </Grid>
        <Grid
          item
          xs={5}
          sm={6}
          md={4}
          lg={4}
          xl={2}
          sx={styles.searchGridItem}
        >
          <Typography sx={styles.fieldLabel}>Invoice Reference:</Typography>
          <Input
            key="invoiceSearch"
            id="invoiceSearch"
            disableUnderline
            value={searchOptions.invoiceReference}
            onChange={(event) =>
              updateSearchOptions({ invoiceReference: event.target.value })
            }
            type={'text'}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="clear search"
                  edge="end"
                  onClick={() => updateSearchOptions({ invoiceReference: '' })}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            }
            placeholder="Invoice Reference Search"
            sx={styles.searchInput}
          />
        </Grid>
        <Grid
          item
          xs={5}
          sm={8}
          md={4}
          lg={4}
          xl={2}
          sx={styles.searchGridItem}
        >
          <Typography sx={styles.fieldLabel}>Global Search:</Typography>
          <Input
            key="searchSearch"
            id="searchSearch"
            disableUnderline
            value={searchOptions.search}
            onChange={(event) =>
              updateSearchOptions({ search: event.target.value })
            }
            type={'text'}
            startAdornment={
              <InputAdornment position="start">
                <SearchSharpIcon fontSize="small" />
              </InputAdornment>
            }
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="clear search"
                  edge="end"
                  onClick={() => updateSearchOptions({ search: '' })}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            }
            placeholder="Search"
            sx={styles.searchInput}
          />
        </Grid>
        <Grid
          item
          xs={5}
          sm={4}
          md={4}
          lg={4}
          xl={2}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-end',
          }}
        >
          <Button
            variant="outlined"
            onClick={() => {
              setClientSelect(defaultClient.companyName)
              updateSearchOptions({
                search: '',
                invoiceReference: '',
                status: null,
              })
              setFromDate(moment(defaultSearch.when?.from))
              setToDate(moment(defaultSearch.when?.to))
            }}
            sx={{ height: '38px', fontWeight: 'bold', mx: 2, my: 0 }}
          >
            Reset Search
          </Button>
        </Grid>
      </Grid>
    </>
  )
}
export default SearchBar
